/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useCallback, useMemo, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $getSelection, $isRangeSelection, TextNode } from 'lexical';
import { INSERT_VARIABLE_COMMAND } from './index';
import type { VariableDefinition } from '../../types';
import { VARIABLE_DEFINITIONS } from '../../constants';
import { createPortal } from 'react-dom';

class VariableTypeaheadOption extends MenuOption {
  variable: VariableDefinition;

  constructor(variable: VariableDefinition) {
    super(variable.key);
    this.variable = variable;
  }
}

const VariableMenuItem = ({
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: VariableTypeaheadOption;
}) => {
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={`variable-typeahead-item ${isSelected ? 'selected' : ''}`}
      ref={option.setRefElement}
      role='option'
      aria-selected={isSelected}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <div className='d-flex align-items-center'>
        <i className={`fa-light ${option.variable.icon} me-2`}></i>
        <div className='variable-content'>
          <div className='variable-label'>{option.variable.label}</div>
          <div className='variable-description text-muted'>{option.variable.description}</div>
        </div>
      </div>
    </li>
  );
};

export const VariableTypeaheadPlugin = (): JSX.Element => {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch('{', {
    minLength: 0,
  });

  const options = useMemo(() => {
    const variableOptions = VARIABLE_DEFINITIONS.map((variable) => new VariableTypeaheadOption(variable));

    if (!queryString) {
      return variableOptions;
    }

    const lowerQuery = queryString.toLowerCase();
    return variableOptions.filter(
      (option) =>
        option.variable.label.toLowerCase().includes(lowerQuery) ||
        option.variable.key.toLowerCase().includes(lowerQuery),
    );
  }, [queryString]);

  const onSelectOption = useCallback(
    (selectedOption: VariableTypeaheadOption, nodeToRemove: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection) || selectedOption === null) {
          return;
        }

        if (nodeToRemove) {
          nodeToRemove.remove();
        }

        editor.dispatchCommand(INSERT_VARIABLE_COMMAND, selectedOption.variable.key);
        closeMenu();
      });
    },
    [editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin<VariableTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForTriggerMatch}
      options={options}
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
        anchorElementRef.current && options.length > 0
          ? createPortal(
              <div className='variable-typeahead-menu'>
                <ul className='list-unstyled mb-0'>
                  {options.map((option, i: number) => (
                    <VariableMenuItem
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </ul>
              </div>,
              anchorElementRef.current,
            )
          : null
      }
    />
  );
};
